import React, {
    createContext, useContext
} from 'react';
import {
    HelpCircle, Tag, Settings2, Zap, History, Filter, Search, ListTree, GitBranch
} from 'lucide-react';
import {
    Label
} from 'shared/components/ui/label';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
    cn
} from 'shared/lib/utils';

/** Step kinds that use the zone card layout in Simple mode. */
export const ZONE_LAYOUT_STEP_KINDS = new Set([
    'llm', 'memory', 'rag', 'api', 'tool', 'choose', 'subflow', 'call', 'parallel'
]);

/** Default step title placeholders per kind (Identity zone). */
export const STEP_TITLE_PLACEHOLDERS = {
    llm: 'e.g. Ask AI',
    memory: 'e.g. Conversation history',
    rag: 'e.g. Search knowledge base',
    api: 'e.g. Fetch order details',
    tool: 'e.g. Run MCP tool',
    choose: 'e.g. Route by intent',
    subflow: 'e.g. Run onboarding flow',
    call: 'e.g. Call shared definition',
    parallel: 'e.g. Parallel lookups'
};

/** Shared focus ring for zone-layout step inputs. */
export const llmFocusInputClass = [
    'border-input bg-white',
    'transition-[box-shadow,border-color] duration-150',
    'focus-visible:border-primary/50',
    'focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0'
].join(' ');

/** Indigo zone fill — slightly lighter/brighter than default `indigo-50` (#eef2ff). */
export const INDIGO_ZONE_PANEL = 'bg-[#f5f8ff]';

const ZONE_META = {
    identity: {
        Icon: Tag,
        iconWrap: 'text-violet-600',
        panel: 'bg-violet-50/90',
        helpBtn: 'bg-violet-500/10 text-violet-600/90 hover:bg-violet-500/15 hover:text-violet-700'
    },
    configuration: {
        Icon: Settings2,
        iconWrap: 'text-indigo-600',
        panel: INDIGO_ZONE_PANEL,
        helpBtn: 'bg-indigo-500/10 text-indigo-600/90 hover:bg-indigo-500/15 hover:text-indigo-700'
    },
    read: {
        Icon: History,
        iconWrap: 'text-indigo-600',
        panel: INDIGO_ZONE_PANEL,
        helpBtn: 'bg-indigo-500/10 text-indigo-600/90 hover:bg-indigo-500/15 hover:text-indigo-700'
    },
    filters: {
        Icon: Filter,
        iconWrap: 'text-violet-600',
        panel: 'bg-violet-50/90',
        helpBtn: 'bg-violet-500/10 text-violet-600/90 hover:bg-violet-500/15 hover:text-violet-700'
    },
    output: {
        Icon: Zap,
        iconWrap: 'text-amber-700',
        panel: 'bg-amber-50/90',
        helpBtn: 'bg-amber-500/10 text-amber-800/85 hover:bg-amber-500/15 hover:text-amber-900'
    },
    search: {
        Icon: Search,
        iconWrap: 'text-sky-700',
        panel: 'bg-sky-50/90',
        helpBtn: 'bg-sky-500/10 text-sky-700/90 hover:bg-sky-500/15 hover:text-sky-800'
    },
    arguments: {
        Icon: ListTree,
        iconWrap: 'text-indigo-600',
        panel: INDIGO_ZONE_PANEL,
        helpBtn: 'bg-indigo-500/10 text-indigo-600/90 hover:bg-indigo-500/15 hover:text-indigo-700'
    },
    branches: {
        Icon: GitBranch,
        iconWrap: 'text-fuchsia-700',
        panel: 'bg-fuchsia-50/90',
        helpBtn: 'bg-fuchsia-500/10 text-fuchsia-700/90 hover:bg-fuchsia-500/15 hover:text-fuchsia-800'
    }
};

const LlmZoneContext = createContext('configuration');

/** @returns {'identity'|'configuration'|'output'} */
export function useLlmZoneVariant() {
    return useContext(LlmZoneContext);
}

function LlmZoneHelpButton({
    help, helpTone, label
}) {
    const zoneVariant = useLlmZoneVariant();
    const toneKey = helpTone || zoneVariant;
    const helpBtnClass = (ZONE_META[toneKey] || ZONE_META.configuration).helpBtn;
    if (!help) return null;
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                            'transition-colors',
                            helpBtnClass
                        )}
                        aria-label={label ? `About ${label}` : 'More help'}
                    >
                        <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    align="start"
                    className="max-w-xs border bg-popover px-3 py-2 text-[11px] leading-snug text-popover-foreground shadow-md"
                >
                    {help}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

/**
 * LLM step editor zone — soft fill, no hard border, icon + friendlier header typography.
 * @param {'identity'|'configuration'|'output'} variant
 */
export function LlmStepZone({
    title, description, help, children, variant = 'configuration', className = ''
}) {
    const meta = ZONE_META[variant] || ZONE_META.configuration;
    const ZoneIcon = meta.Icon;

    return (
        <LlmZoneContext.Provider value={variant}>
            <div className={cn(
                'rounded-xl p-3',
                meta.panel,
                className
            )}>
                <div className="mb-2.5 flex items-start gap-2">
                    <span className={cn(
                        'inline-flex h-8 w-8 shrink-0 items-center justify-center',
                        meta.iconWrap
                    )}>
                        <ZoneIcon className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                    </span>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <h3 className="text-[14px] font-bold leading-tight tracking-tight text-foreground">
                                {title}
                            </h3>
                            <LlmZoneHelpButton help={help} label={title} />
                        </div>
                        {description && (
                            <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
                                {description}
                            </p>
                        )}
                    </div>
                </div>
                <div className="space-y-3">{children}</div>
            </div>
        </LlmZoneContext.Provider>
    );
}

/** Standalone (?) tooltip — for section headers and inline hints. */
export function HelpTooltip({
    content, ariaLabel = 'Help', tone = 'configuration'
}) {
    if (!content) return null;
    const helpBtnClass = (ZONE_META[tone] || ZONE_META.configuration).helpBtn;
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'inline-flex h-5 w-5 items-center justify-center rounded-full',
                            'transition-colors',
                            helpBtnClass
                        )}
                        aria-label={ariaLabel}
                        onClick={(e) => e.preventDefault()}
                    >
                        <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" align="center" sideOffset={6}>
                    <p className="text-xs leading-relaxed">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

/** Field label with optional help in a hover tooltip — tone matches parent zone. */
export function LlmFieldLabel({
    label, help, helpTone
}) {
    const zoneVariant = useLlmZoneVariant();
    const toneKey = helpTone || zoneVariant;
    const helpBtnClass = (ZONE_META[toneKey] || ZONE_META.configuration).helpBtn;
    return (
        <div className="flex items-center gap-1.5">
            <Label className="text-[11px] font-semibold text-foreground/70">
                {label}
            </Label>
            {help && (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className={cn(
                                    'inline-flex h-5 w-5 items-center justify-center rounded-full',
                                    'transition-colors',
                                    helpBtnClass
                                )}
                                aria-label={`About ${label}`}
                            >
                                <HelpCircle className="h-3.5 w-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="center" sideOffset={6}>
                            <p className="text-xs leading-relaxed">{help}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}

export function LlmLabeled({
    label, help, helpTone, children
}) {
    return (
        <div className="space-y-1">
            <LlmFieldLabel label={label} help={help} helpTone={helpTone} />
            {children}
        </div>
    );
}

/** Wraps zone-layout step editor body. */
export function LlmStepCanvas({
    children, className = ''
}) {
    return (
        <div className={cn(
            'space-y-3 rounded-xl bg-white/70 p-0.5',
            className
        )}>
            {children}
        </div>
    );
}

/** Zone section — drop-in replacement for FieldSection in zone-layout steps. */
export function StepFieldZone({
    title, description, hint, children, variant = 'configuration', className = ''
}) {
    return (
        <LlmStepZone
            variant={variant}
            title={title}
            description={description || hint}
            className={className}
        >
            {children}
        </LlmStepZone>
    );
}
